'use client'

import React, { useEffect, useRef } from 'react'
import type { FWImageBlock as FWImageBlockType, Media } from '@/payload-types'

import './FWImage.scss'

type FWImageClientProps = FWImageBlockType & {
  id?: string
}

export const FWImageClient: React.FC<FWImageClientProps> = ({
  height = 'normal',
  backgroundImage,
  filterColor = 'danger',
  filterOpacity = '0.8',
  quoteTag = 'h3',
  quote,
  enableParallax = true,
}) => {
  const sectionRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!enableParallax || !sectionRef.current) return

    const handleScroll = () => {
      const section = sectionRef.current
      if (!section) return

      const scrolled = window.pageYOffset
      const sectionTop = section.offsetTop
      const sectionHeight = section.offsetHeight

      if (scrolled + window.innerHeight > sectionTop && scrolled < sectionTop + sectionHeight) {
        const yPos = (scrolled - sectionTop) * 0.6
        section.style.backgroundPosition = `center calc(50% + ${yPos}px)`
      }
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [enableParallax])

  // Get background image URL
  const bgImageUrl =
    typeof backgroundImage === 'object' && backgroundImage !== null
      ? (backgroundImage as Media).url
      : ''

  // Map filter color to CSS class
  const filterClass = `color-${filterColor}`

  // Map height to CSS class
  const heightClass = height

  // Map opacity to CSS class
  const opacityClass = `opacity-${filterOpacity.replace('.', '-')}`

  // Determine text color based on filter color
  const lightColors = ['warning', 'success', 'info']
  const textColorClass = lightColors.includes(filterColor) ? 'text-dark' : 'text-white'

  // Dynamic heading tag
  const QuoteTag = quoteTag as React.ElementType

  return (
    <div
      ref={sectionRef}
      className={`fwImage ${filterClass} ${heightClass} ${opacityClass}`}
      style={{
        backgroundImage: `url(${bgImageUrl})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundAttachment: enableParallax ? 'fixed' : 'scroll',
      }}
    >
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-7 col-md-9">
            <div className="text-center py-4">
              <QuoteTag className={`fwTitle ${textColorClass}`}>{quote}</QuoteTag>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
